
import { Injectable, signal, computed, effect, OnDestroy, Inject, DOCUMENT } from '@angular/core';
import { Subject } from 'rxjs';
import { BreakpointService } from './breakpoint.service';

export interface LayoutConfig {
  darkTheme?: boolean;
  menuMode?: 'static' | 'overlay';
}

@Injectable({
  providedIn: 'root'
})
export class LayoutService implements OnDestroy {
  // Configuration
  _config = signal<LayoutConfig>({
    darkTheme: false,
    menuMode: 'static'
  });

  // États du menu - temporairement public pour debug
  public _isMenuCollapsed = signal<boolean>(false);
  private _isMobileMenuOpen = signal<boolean>(false);
  private _isDesktop = signal<boolean>(true);
  private _isFullscreen = signal<boolean>(false);

  // Menu state management
  menuSource = new Subject<any>();
  resetSource = new Subject<any>();

  // Observables pour compatibilité
  overlayOpen = new Subject<any>();
  overlayOpen$ = this.overlayOpen.asObservable();
  menuSource$ = this.menuSource.asObservable();
  resetSource$ = this.resetSource.asObservable();

  // Computed
  public isMobile = computed(() => !this._isDesktop());
  public isDesktop = computed(() => this._isDesktop());
  public isDarkTheme = computed(() => this._config().darkTheme);
  public isMenuCollapsed = computed(() => this._isMenuCollapsed() && this._isDesktop());
  public isMobileMenuOpen = computed(() => this._isMobileMenuOpen() && !this._isDesktop());
  public isFullscreen = computed(() => this._isFullscreen());


  // Classes CSS pour les composants
  containerClasses = computed(() => ({
    'menu-collapsed': this.isMenuCollapsed(),
    'mobile-menu-open': this.isMobileMenuOpen(),
    'dark-theme': this.isDarkTheme(),
    'is-desktop': this.isDesktop(),
    'is-mobile': this.isMobile()
  }));

  private resizeListener?: () => void;
  private fullscreenListeners: (() => void)[] = [];

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private breakpointService: BreakpointService
  ) {
    this.initializeResponsive();
    this.initializeFullscreen();

    // this._isMenuCollapsed.set(true);

    // Effect pour le dark mode
    effect(() => {
      this.applyDarkMode(this._config().darkTheme);
    });
  }

  // === MENU STATE MANAGEMENT ===
  onMenuStateChange(event: any): void {
    this.menuSource.next(event);
  }

  resetMenu(): void {
    this.resetSource.next(null);
  }

  // === MENU MANAGEMENT ===
  toggleDesktopMenu(): void {
    if (this._isDesktop()) {
      this._isMenuCollapsed.set(!this._isMenuCollapsed());
      // Test immédiat
      setTimeout(() => {
      }, 100);
    }
  }

  toggleMobileMenu(): void {
    if (!this._isDesktop()) {
      this._isMobileMenuOpen.update(open => !open);
      if (this._isMobileMenuOpen()) {
        this.overlayOpen.next(null);
      }
    }
  }

  closeMobileMenu(): void {
    this._isMobileMenuOpen.set(false);
  }

  // === RESPONSIVE ===
  private initializeResponsive(): void {
    if (typeof window !== 'undefined') {
      this.updateScreenSize();

      this.resizeListener = () => {
        this.updateScreenSize();
        this.handleResponsiveChanges();
      };

      window.addEventListener('resize', this.resizeListener);
    }
  }

  private updateScreenSize(): void {
    if (typeof window !== 'undefined') {
      const lgBreakpoint = this.breakpointService.getBreakpointValue('lg');
      this._isDesktop.set(window.innerWidth >= lgBreakpoint);
    }
  }

  private handleResponsiveChanges(): void {
    // Fermer le menu mobile si on passe en desktop
    if (this._isDesktop() && this._isMobileMenuOpen()) {
      this._isMobileMenuOpen.set(false);
    }

    // Réinitialiser le collapse si on passe en mobile
    // if (!this._isDesktop() && this._isMenuCollapsed()) {
    //   this._isMenuCollapsed.set(false);
    // }
  }

  // === DARK MODE ===
  toggleDarkMode(): void {
    this._config.update(config => ({
      ...config,
      darkTheme: !config.darkTheme
    }));
  }

  private applyDarkMode(isDark?: boolean): void {
    if (typeof document !== 'undefined') {
      if (isDark) {
        document.documentElement.classList.add('app-dark');
      } else {
        document.documentElement.classList.remove('app-dark');
      }
    }
  }

  // === FULLSCREEN ===
  private initializeFullscreen(): void {
    if (typeof document !== 'undefined') {
      const listeners = [
        () => document.addEventListener('fullscreenchange', () => this.updateFullscreenState()),
        () => document.addEventListener('webkitfullscreenchange', () => this.updateFullscreenState()),
        () => document.addEventListener('mozfullscreenchange', () => this.updateFullscreenState()),
        () => document.addEventListener('MSFullscreenChange', () => this.updateFullscreenState())
      ];

      listeners.forEach(addListener => {
        addListener();
        this.fullscreenListeners.push(addListener);
      });
    }
  }

  private updateFullscreenState(): void {
    const isCurrentlyFullscreen = !!(
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement
    );

    this._isFullscreen.set(isCurrentlyFullscreen);
  }

  openFullscreen(): void {
    const elem = document.documentElement;

    if (elem?.requestFullscreen) {
      elem.requestFullscreen().catch(err => console.error('Fullscreen error:', err));
    } else if ((elem as any)?.webkitRequestFullscreen) {
      (elem as any).webkitRequestFullscreen();
    } else if ((elem as any)?.mozRequestFullScreen) {
      (elem as any).mozRequestFullScreen();
    } else if ((elem as any)?.msRequestFullscreen) {
      (elem as any).msRequestFullscreen();
    }
  }

  closeFullscreen(): void {
    if (document?.exitFullscreen) {
      document.exitFullscreen();
    } else if ((document as any)?.webkitExitFullscreen) {
      (document as any).webkitExitFullscreen();
    } else if ((document as any)?.mozCancelFullScreen) {
      (document as any).mozCancelFullScreen();
    } else if ((document as any)?.msExitFullscreen) {
      (document as any).msExitFullscreen();
    }
  }

  // === CLEANUP ===
  ngOnDestroy(): void {
    if (this.resizeListener && typeof window !== 'undefined') {
      window.removeEventListener('resize', this.resizeListener);
    }
  }
}
